import { TextField as MuiTextField } from '@mui/material';
import { DatePicker as MuiDatePicker, DatePickerProps as MuiDatePickerProps } from '@mui/x-date-pickers/DatePicker';
import React from 'react';

import { Merge } from 'type-fest';

import { DateFormatEnum } from '../../../utils/date.utils';
import { TextFieldProps } from '../../TextField/TextField';
import { OverrideDatePickerCommonProps } from '../datePickerAdapter';
import {
  PickerProps,
  useBaseAdapterProps,
  useDateBoundProps,
  useMonthAdapterProps,
  usePickerProps
} from '../picker.hooks';

type MuiDatePickerPropsSlotProps = Exclude<MuiDatePickerProps['slotProps'], undefined>;
type DatePickerPropsSlotProps = Omit<MuiDatePickerPropsSlotProps, 'field' | 'textField'>;
type DatePickerPropsSlots = Omit<Exclude<MuiDatePickerProps['slots'], undefined>, 'field' | 'textField'>;

interface DatePickerProps extends OverrideDatePickerCommonProps<MuiDatePickerProps> {
  clearable?: PickerProps['clearable'];
  slotProps?: DatePickerPropsSlotProps;
  slots?: DatePickerPropsSlots;
  textFieldProps?: Merge<
    TextFieldProps,
    {
      /** @default true */
      fullWidth?: TextFieldProps['fullWidth'];
    }
  >;
}

export const DatePicker = (props: DatePickerProps) => {
  const {
    clearable,
    slots,
    slotProps,
    timezone,
    minDate,
    maxDate,
    textFieldProps,
    value,
    onChange,
    onMonthChange,
    ...restOfProps
  } = props;

  const { openPickerButton: slotPropsOpenPickerButton, ...restOfSlotProps } = slotProps || {};

  const { slotPropsTextFieldOverrides, ...pickerProps } = usePickerProps({ clearable, value, textFieldProps });

  const baseAdapterProps = useBaseAdapterProps({ timezone, value, onChange });
  const monthAdapterProps = useMonthAdapterProps({ onMonthChange });
  const dateBoundProps = useDateBoundProps({ minDate, maxDate, timezone });

  return (
    <MuiDatePicker
      format={DateFormatEnum.DateWeekday}
      {...baseAdapterProps}
      {...monthAdapterProps}
      {...dateBoundProps}
      {...restOfProps}
      slotProps={{
        ...restOfSlotProps,
        field: pickerProps.slotPropsField,
        openPickerButton: { ...slotPropsOpenPickerButton, ref: pickerProps.slotPropsOpenPickerButtonRef },
        textField: slotPropsTextFieldOverrides
      }}
      closeOnSelect={true}
      // TODO: see https://mui.com/x/react-date-pickers/custom-field/#using-material-textfield
      enableAccessibleFieldDOMStructure={false}
      slots={{ ...slots, textField: MuiTextField }}
    />
  );
};
